import {
	Award,
	BarChart3,
	Coins,
	Flag,
	Layers,
	LayoutDashboard,
	LayoutGrid,
	LineChart,
	Package,
	PhoneCall,
	RefreshCw,
	ShieldCheck,
	Sparkles,
	Stethoscope,
	Store,
	Swords,
	Tags,
	Target,
	Users,
	Workflow,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { fetchJson } from "../../api/rest";
import type { FlowStats } from "../../types/flows";
import { BrandMark } from "./BrandMark";

/**
 * Flows has eight sections, which is too many for a tab strip and exactly right for a fold-out:
 * they appear under Flows while you are in it and take no room when you are not. `end` marks the
 * one that also covers an individual flow, since a flow belongs to the list it came from.
 */
interface FlowsSection {
	label: string;
	path: string;
	/** Also covers an individual flow, which belongs to the list it came from. */
	end?: boolean;
	/** Which count from /flows/stats to show beside it. */
	badge?: "pendingApprovals" | "newCandidates";
}

const FLOWS_SECTIONS: FlowsSection[] = [
	{ label: "List", path: "/flows", end: true },
	{ label: "Templates", path: "/flows/templates" },
	{ label: "Runs", path: "/flows/runs" },
	{ label: "Comparisons", path: "/flows/comparisons", badge: "newCandidates" },
	{ label: "SEO queue", path: "/flows/seo" },
	{ label: "Approvals", path: "/flows/approvals", badge: "pendingApprovals" },
	{ label: "Schedule", path: "/flows/schedule" },
	{ label: "Calendar", path: "/flows/calendar" },
	{ label: "Settings", path: "/flows/settings" },
];

export const navSections = [
	{
		label: "Menu",
		items: [
			{ label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
			{ label: "Categories", icon: Tags, path: "/categories" },
			{ label: "Analytics", icon: BarChart3, path: "/analytics" },
			{ label: "SEO & affiliate", icon: LineChart, path: "/metrics" },
			{ label: "Revenue Channels", icon: Coins, path: "/revenue" },
			{ label: "CSS status", icon: ShieldCheck, path: "/css-status" },
			{ label: "Recommendations", icon: Sparkles, path: "/recommendations" },
			{ label: "Planner", icon: Target, path: "/planner" },
		],
	},
	{
		label: "Sales",
		items: [{ label: "Cold calls", icon: PhoneCall, path: "/cold-calls" }],
	},
	{
		label: "Monitoring",
		items: [
			{ label: "Competition", icon: Swords, path: "/competition" },
			{ label: "Reported issues", icon: Flag, path: "/issue-reports" },
			{ label: "Diagnostics", icon: Stethoscope, path: "/diagnostics" },
			{ label: "Cache", icon: RefreshCw, path: "/cache" },
		],
	},
	{
		label: "Shop",
		items: [
			{ label: "Products", icon: Package, path: "/products" },
			{ label: "Product lines", icon: Layers, path: "/product-lines" },
			{ label: "Brands", icon: Award, path: "/brands" },
			{ label: "Brand pages", icon: LayoutGrid, path: "/brand-pages" },
			{ label: "Merchants", icon: Store, path: "/merchants" },
			{ label: "Customers", icon: Users, path: "/customers" },
		],
	},
	{
		label: "Automation",
		items: [
			{
				label: "Flows",
				icon: Workflow,
				path: "/flows",
				children: FLOWS_SECTIONS,
			},
		],
	},
];

interface SidebarProps {
	collapsed: boolean;
}

export const Sidebar = ({ collapsed }: SidebarProps) => {
	const location = useLocation();
	const [flowStats, setFlowStats] = useState<FlowStats | null>(null);
	const [newIssues, setNewIssues] = useState(0);

	// Pending approvals hold a run open and new candidates are work waiting to be
	// judged; both are invisible unless the sidebar says so. Polled rather than
	// refreshed on navigation, because a run reaches an Approval node while the
	// admin sits on an unrelated page.
	useEffect(() => {
		const refresh = () =>
			fetchJson<FlowStats>("/flows/stats")
				.then(setFlowStats)
				.catch(() => {
					// A badge is not worth an error state.
				});
		const refreshIssues = () =>
			fetchJson<{ count: number }>("/issue-reports/new-count")
				.then(({ count }) => setNewIssues(count))
				.catch(() => {});
		refresh();
		refreshIssues();
		const timer = setInterval(() => {
			refresh();
			refreshIssues();
		}, 60_000);
		return () => clearInterval(timer);
	}, []);

	return (
		<aside
			className={`fixed top-0 left-0 z-40 h-screen transition-all duration-300 ease-in-out ${
				collapsed ? "w-[70px]" : "w-[250px]"
			} flex flex-col border-r border-white/10 bg-sidebar`}
		>
			{/* The storefront's own mark, so the admin and the shop are the same product. The
			    full lockup collapses to the icon alone, which is what the 161x48 logo crops to. */}
			<div
				className={`flex h-16 shrink-0 items-center justify-center border-b border-white/10 text-[#e7e8e3] ${
					collapsed ? "px-0" : "px-6"
				}`}
			>
				<BrandMark iconOnly={collapsed} />
			</div>

			{/* Navigation */}
			<nav className="scrollbar-thin flex-1 overflow-y-auto py-2">
				{navSections.map((section) => (
					<div key={section.label}>
						{!collapsed && (
							<p className="px-6 pt-5 pb-2 text-[10.5px] font-medium uppercase tracking-[0.18em] text-sidebar-title">
								{section.label}
							</p>
						)}
						<div className="space-y-0.5">
							{section.items.map((item) => {
								// Flows is one entry over a whole sub-tree, so it stays lit on
								// /flows/runs/12 as well as on /flows.
								const isActive =
									location.pathname === item.path ||
									location.pathname.startsWith(`${item.path}/`);
								const children = "children" in item ? item.children : undefined;
								// The parent count is a summary of the children's. Once they are on
								// screen with their own counts it would be the same number twice.
								const badge =
									item.path === "/flows" && !(isActive && !collapsed)
										? (flowStats?.pendingApprovals ?? 0) +
											(flowStats?.newCandidates ?? 0)
										: item.path === "/issue-reports"
											? newIssues
											: 0;
								return (
									<div key={item.path}>
										<NavLink
											to={item.path}
											className={`group relative mx-3 flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors duration-200 ${
												collapsed ? "justify-center" : ""
											} ${
												isActive
													? "bg-white/[0.06] font-medium text-white"
													: "text-sidebar-link hover:bg-white/[0.04] hover:text-white"
											}`}
										>
											{isActive && (
												<span className="absolute top-1/2 -left-3 h-5 -translate-y-1/2 border-l-2 border-brand" />
											)}
											<item.icon
												className={`h-[18px] w-[18px] shrink-0 transition-colors ${
													isActive
														? "text-brand"
														: "text-sidebar-icon group-hover:text-zinc-300"
												}`}
											/>
											{!collapsed && (
												<span className="truncate">{item.label}</span>
											)}
											{badge > 0 && (
												<span
													className={`flex items-center gap-1.5 text-[11px] text-zinc-400 ${
														collapsed ? "absolute top-1 right-1" : "ml-auto"
													}`}
													title={
														item.path === "/flows"
															? "Pending approvals and new comparison candidates"
															: "New reports"
													}
												>
													<span className="size-1.5 rounded-full bg-red-500" />
													{!collapsed && (badge > 99 ? "99+" : badge)}
												</span>
											)}
										</NavLink>

										{children && isActive && !collapsed && (
											<div className="mt-1 mb-2 ml-[2.4rem] space-y-0.5 border-l border-white/10 pl-3">
												{children.map((child) => {
													const count =
														child.badge === "pendingApprovals"
															? (flowStats?.pendingApprovals ?? 0)
															: child.badge === "newCandidates"
																? (flowStats?.newCandidates ?? 0)
																: 0;
													// An individual flow belongs to the list it came from, so
													// /flows/12 keeps List lit rather than nothing at all.
													const here = child.end
														? location.pathname === "/flows" ||
															/^\/flows\/\d+/.test(location.pathname)
														: location.pathname === child.path ||
															location.pathname.startsWith(`${child.path}/`);
													return (
														<NavLink
															key={child.path}
															to={child.path}
															className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] transition-colors ${
																here
																	? "font-medium text-white"
																	: "text-zinc-500 hover:text-white"
															}`}
														>
															<span className="flex-1 truncate">
																{child.label}
															</span>
															{count > 0 && (
																<span className="flex items-center gap-1.5 text-[11px] text-zinc-400">
																	<span className="size-1.5 rounded-full bg-red-500" />
																	{count > 99 ? "99+" : count}
																</span>
															)}
														</NavLink>
													);
												})}
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				))}
			</nav>

			{!collapsed && (
				<div className="border-t border-white/10 px-6 py-4">
					<p className="text-[10.5px] uppercase tracking-[0.18em] text-sidebar-icon">
						Admin · v1.0.0
					</p>
				</div>
			)}
		</aside>
	);
};
